// Kinio 即時影像：RTCPeerConnection 佈線（docs/KINIO-API.md §4）。
//
// 流程（順序有意義）：
//   1. `new RTCPeerConnection()`（**iceServers 先留空**——契約：一律用 response 裡的
//      `ice_servers`，不自己算、不寫死）
//   2. `addTransceiver('video'|'audio', {direction:'recvonly'})` → `createOffer()`
//      要對講的那一路（`talk: true`）：audio 改 sendrecv（軌先空著，按下才換成麥克風）+ `createDataChannel('ls-ctrl')`（lsctrl.ts）
//   3. `command start_viewing {sdp:{type:'offer',sdp}, connection_attempt_id}`
//   4. response → `setConfiguration({iceServers})` → `setLocalDescription(offer)`
//      （設定完才 setLocalDescription，本地 gathering 才會用到平台派的 TURN）
//      → `setRemoteDescription(answer)` → flush 先前緩衝的本地 candidate
//   5. 雙向 trickle `ice_candidate`（`candidate: null` = 對端收集完成）
//   6. 離開 → `command stop_viewing {session_id}` + `pc.close()`
//
// dev emulator 只回 data channel（echo），沒有真的影像 frame；`hasRemoteVideo` /
// `receivingVideo` 讓 UI 能顯示「已連線但沒有影像軌」而不是卡在轉圈。

import { KinioError } from './errors';
import { LENS_ID_BASE, LensSwitch, companionEchoOk, parseLsCtrlLens, type LensPhase, type LensState } from './lens';
import { LS_CTRL_LABEL, LS_CTRL_PROTOCOL, TalkControl } from './lsctrl';
import { closeErrorCode, isTokenExpired, sharedSignal, signalError, type IceCandidateFrame, type MediaSignal, type SignalErrorFrame, type SignalState } from './signal';

export type LivePhase =
  | 'idle'
  /** 連 signaling / signin */
  | 'signaling'
  /** 已送 start_viewing，等 answer */
  | 'negotiating'
  /** answer 已套用，ICE 連線中 */
  | 'ice'
  /** peer connection connected */
  | 'connected'
  | 'failed'
  | 'stopped';

/**
 * 對講狀態（UI 用）：busy = 別的觀看者正在對講（ls-ctrl TALK_BUSY）；denied = 瀏覽器不給麥克風；
 * forbidden = 伺服器說這個人沒有 media.talk（start_viewing response 的 talk:false）；unavailable = 還沒連上 / 這一路沒協商對講。
 */
export type TalkState = 'off' | 'starting' | 'on' | 'busy' | 'denied' | 'forbidden' | 'unavailable' | 'error';

export interface LiveCallbacks {
  onPhase?: (phase: LivePhase) => void;
  onTalk?: (state: TalkState) => void;
  onStream?: (stream: MediaStream | null) => void;
  onError?: (err: KinioError) => void;
  /** pc.iceConnectionState / pc.connectionState 變化（UI 顯示用）。 */
  onConnectionState?: (s: { ice: RTCIceConnectionState; pc: RTCPeerConnectionState }) => void;
  /** start_viewing 回來了：session_id（media-ready 用）與 `talk`（使用者有沒有 media.talk；null = 伺服端沒說）。 */
  onSession?: (s: { sessionId: string | null; talkGranted: boolean | null }) => void;
  /** 設備 ls-ctrl hello 的 `caps.talk`。 */
  onHello?: (talkCapable: boolean) => void;
  /** 多鏡頭：ls-ctrl hello 的 `caps.lens`（null = 沒宣告 = 只有主鏡頭 / 舊韌體，鏡頭切換停用）。 */
  onLensHello?: (lenses: number[] | null) => void;
  /** 多鏡頭：ls-ctrl state 的鏡頭部分（lens_active / lens_blocked / companion_admit / companion）。 */
  onLensState?: (state: LensState) => void;
  /** 多鏡頭：單畫面切換鏡頭的狀態（已顯示 / 切換中 / 失敗）。 */
  onLensPhase?: (phase: LensPhase) => void;
}

export interface LiveOptions extends LiveCallbacks {
  apiEndpoint: string;
  homeId: string;
  /** Thing 的定址鍵（通道 = `<parent>:chN`，SPEC #terms）。deviceRef / aid 給一個（aid 是 home-base 的舊名）。 */
  deviceRef?: string;
  aid?: string;
  /** 測試 / 多格共用時注入；預設用 `sharedSignal(apiEndpoint, homeId)`。 */
  signal?: MediaSignal;
  /** 測試注入（預設 globalThis.RTCPeerConnection）。 */
  PeerConnectionImpl?: typeof RTCPeerConnection;
  /** 要不要協商音訊（預設 true）。 */
  audio?: boolean;
  /**
   * 要不要能對講（預設 false；要 audio）：audio m-line 改 sendrecv + 開 ls-ctrl DataChannel（plan SPEC 設備呼叫「接聽後的媒體與音訊」、
   * vms-web LIVE-07）。只在真的會用到對講的那一路開——每一路多一條 DataChannel。
   */
  talk?: boolean;
  /** 測試注入（預設 navigator.mediaDevices.getUserMedia 取麥克風）。 */
  getMicrophone?: () => Promise<MediaStream>;
  /** 碼流（vms-web LIVE-02）：小格子要子碼流、單一放大要主碼流；不帶 = 設備預設（主碼流）。舊設備忽略這個欄位。 */
  stream?: LiveStream;
  commandTimeoutMs?: number;
  /**
   * VMS façade（plan/vms-system-v0.md §5.1）：每次開始觀看前先預留 session，拿一次性 ticket 帶進 start_viewing
   * （`vmsReserve(api, home, device)`）。沒給 = 舊路徑（直接 start_viewing）。預留失敗 → 這一次觀看失敗（不偷偷退回舊路徑）。
   */
  reserve?: () => Promise<{ ticket: string; sessionId?: string }>;
  /** 多鏡頭（plan SPEC #multi-lens-companion）：start_viewing 帶 `lens`（0–7）；沒帶 = 設備預設主鏡頭。單鏡頭設備不要帶。 */
  lens?: number;
  /**
   * 雙畫面的伴隨連線：主連線的 session_id。伴隨只要影像（不協商音訊、不開 DataChannel），一定帶 `lens`；
   * hub 的 response 要回送同樣的 `lens` 與 `primary_session_id`，對不上 → stop_viewing、`COMPANION_MISMATCH` 失敗。
   */
  primarySessionId?: string;
  /** 多鏡頭的主連線：沒對講也開 ls-ctrl（換鏡頭 `select {lens}`、收 state）。 */
  lensControl?: boolean;
  /** 測試注入的單調時鐘（毫秒；預設 performance.now()）——觀看計時用（SPEC #media-live-ttff）。 */
  now?: () => number;
}

export type LiveStream = 'main' | 'sub';

/**
 * `stop_viewing` 的停止原因（`payload.reason`，選填；plan SPEC #media-conn-stats「停止原因」）。平台靠它分「放棄」與「連不上」：
 * `connect_timeout`（15 秒連不上提示後重試或離開）、`retry` 算失敗；其他是觀看端自己收掉。不帶 = 平台照 setup 時間猜。
 */
export type LiveStopReason = 'user_leave' | 'background' | 'multiview_leave' | 'stream_switch' | 'connect_timeout' | 'retry';

/** SDP 裡有沒有某種 media（`m=video …` 且 port ≠ 0）。 */
export function sdpHasMedia(sdp: string | undefined | null, kind: 'video' | 'audio' | 'application'): boolean {
  if (!sdp) return false;
  for (const line of sdp.split(/\r?\n/)) {
    if (!line.startsWith('m=' + kind + ' ')) continue;
    const port = Number(line.slice(('m=' + kind + ' ').length).split(' ')[0]);
    if (port !== 0) return true;
  }
  return false;
}

export interface ViewingGrant {
  sessionId: string | null;
  iceServers: RTCIceServer[];
  answer: RTCSessionDescriptionInit;
  /** 伺服器對這個人的對講授權（media.talk）：false = 不准（hub 已把 offer 的 audio 改成 recvonly）；null = 伺服器沒說（舊平台）。 */
  talk: boolean | null;
  /** 同 talk（home-base 的名字）。 */
  talkGranted: boolean | null;
  /** hub 回送的 `lens` / `primary_session_id`（伴隨連線核對用；舊 hub 不帶 = null）。 */
  lens: number | null;
  primarySessionId: string | null;
}

/** 驗證 start_viewing 的 response payload；缺 sdp / session_id 就是終態錯誤。 */
export function extractGrant(payload: Record<string, unknown> | undefined): ViewingGrant {
  const p = (payload ?? {}) as {
    session_id?: string;
    ice_servers?: RTCIceServer[];
    sdp?: string | RTCSessionDescriptionInit;
    answer?: string | RTCSessionDescriptionInit;
    talk?: boolean;
    lens?: number;
    primary_session_id?: string;
  };
  const raw = p.sdp ?? p.answer;
  if (!raw) throw new KinioError(502, 'NO_SDP_ANSWER', 'start_viewing response has no sdp answer');
  return {
    sessionId: typeof p.session_id === 'string' ? p.session_id : null,
    iceServers: Array.isArray(p.ice_servers) ? p.ice_servers : [],
    answer: typeof raw === 'string' ? { type: 'answer', sdp: raw } : raw,
    talk: typeof p.talk === 'boolean' ? p.talk : null,
    talkGranted: typeof p.talk === 'boolean' ? p.talk : null,
    lens: typeof p.lens === 'number' ? p.lens : null,
    primarySessionId: typeof p.primary_session_id === 'string' && p.primary_session_id ? p.primary_session_id : null,
  };
}

let attemptSeq = 0;

/**
 * 一路觀看 session。`start()` 之後：phase idle → signaling → negotiating → ice → connected。
 * `stop()` 會送 stop_viewing 並關掉 pc（可再 `start()`）。
 */
/**
 * 一次觀看的計時（SPEC #media-live-ttff）：startedAt = 按下（start()）、answerAt = 收到設備 answer、connectedAt = ICE / pc connected。
 * 時間都是 LiveView.now() 的單調時鐘毫秒；reported = 這一次已經送過 media-ready 或 live-reports（一次觀看只報一筆）。
 */
export interface LiveAttempt {
  id: string;
  startedAt: number;
  answerAt?: number;
  connectedAt?: number;
  /** 用了預先連好的信令（start 時信令已經 open） */
  warm: boolean;
  /** hub session（VMS 預留的優先，否則 start_viewing 回的）；停掉後 LiveView.session 會清掉，這裡留著給回報用 */
  sessionId?: string;
  reported: boolean;
}

function randomAttemptId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c?.randomUUID) return c.randomUUID();
  return `att-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}

export class LiveView {
  readonly deviceRef: string;
  readonly homeId: string;
  readonly apiEndpoint: string;
  private readonly signal: MediaSignal;
  private readonly cb: LiveCallbacks;
  private readonly PC: typeof RTCPeerConnection;
  private readonly wantAudio: boolean;
  private readonly wantTalk: boolean;
  private readonly getMicrophone: () => Promise<MediaStream>;
  private audioSender: RTCRtpSender | null = null;
  private ctrl: RTCDataChannel | null = null;
  private talkCtl: TalkControl | null = null;
  private mic: MediaStreamTrack | null = null;
  private _talk: TalkState = 'off';
  private talkGrant: boolean | null = null;
  private readonly commandTimeoutMs?: number;
  private readonly stream_?: 'main' | 'sub';
  private readonly reserve_?: () => Promise<{ ticket: string; sessionId?: string }>;
  /** start_viewing 帶的鏡頭：重連時回到斷線前在看的那顆（SPEC #multi-lens-dual 連線生命週期）。 */
  private lens_?: number;
  private readonly primarySessionId_?: string;
  private readonly wantLensCtl: boolean;
  private lensSw: LensSwitch | null = null;
  private lensTimer: ReturnType<typeof setInterval> | null = null;
  private lensCaps_: number[] | null = null;
  private lensState_: LensState | null = null;
  /** 最近一次 façade 預留的 session（UI 用它 DELETE 收尾）。 */
  reservedSessionId: string | null = null;
  /** 這一路已因媒體租期到了安靜重開過（避免無限重開）；連上後歸零。 */
  private leaseRestarted = false;
  /** 這一次觀看的計時（觀看端首幀遙測，SPEC #media-live-ttff；ttff.ts 讀）。每次 start() 重新開一筆。 */
  private attempt_: LiveAttempt | null = null;
  private readonly now_: () => number;

  private pc: RTCPeerConnection | null = null;
  private gen = 0;
  private sessionId: string | null = null;
  private pendingLocalIce: (RTCIceCandidateInit | null)[] = [];
  private offs: Array<() => void> = [];
  private _phase: LivePhase = 'idle';
  private disposed = false;

  /** answer 裡有沒有 video m-line（emulator = 沒有 → UI 顯示「無影像軌」）。 */
  hasRemoteVideo = false;
  hasRemoteAudio = false;
  stream: MediaStream | null = null;
  lastError: KinioError | null = null;

  constructor(opts: LiveOptions) {
    const ref = opts.deviceRef ?? opts.aid;
    if (!ref) throw new KinioError(0, 'NO_DEVICE_REF', 'LiveView needs deviceRef');
    this.deviceRef = ref;
    this.homeId = opts.homeId;
    this.apiEndpoint = opts.apiEndpoint;
    this.signal = opts.signal ?? sharedSignal(opts.apiEndpoint, opts.homeId);
    this.cb = opts;
    this.PC = opts.PeerConnectionImpl ?? (globalThis.RTCPeerConnection as typeof RTCPeerConnection);
    this.primarySessionId_ = opts.primarySessionId || undefined;
    this.lens_ = opts.lens ?? (this.primarySessionId_ ? 0 : undefined);
    // 伴隨只要影像：不協商音訊、不開 DataChannel（SPEC #multi-lens-companion）
    this.wantAudio = opts.audio !== false && !this.primarySessionId_;
    this.wantTalk = this.wantAudio && opts.talk === true;
    this.wantLensCtl = !this.primarySessionId_ && opts.lensControl === true;
    this.getMicrophone =
      opts.getMicrophone ??
      (() => {
        const md = globalThis.navigator?.mediaDevices;
        if (!md?.getUserMedia) return Promise.reject(new KinioError(0, 'NO_MICROPHONE', 'getUserMedia unavailable'));
        return md.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false });
      });
    this.commandTimeoutMs = opts.commandTimeoutMs;
    this.stream_ = opts.stream;
    this.reserve_ = opts.reserve;
    this.now_ = opts.now ?? (() => (globalThis.performance ? globalThis.performance.now() : Date.now()));
  }

  /** 這一次觀看（最近一次 start()）的計時；還沒 start 過 = null。 */
  get attempt(): LiveAttempt | null {
    return this.attempt_;
  }

  /** 單調時鐘（跟 attempt 的時間同一個基準；首幀時間用它記）。 */
  now(): number {
    return this.now_();
  }

  /** 同 deviceRef（home-base 的舊名）。 */
  get aid(): string {
    return this.deviceRef;
  }

  /** 目前這一路的 start_viewing session_id（還沒拿到 / 已停 = null）。 */
  get session(): string | null {
    return this.sessionId;
  }

  get phase(): LivePhase {
    return this._phase;
  }

  get peerConnection(): RTCPeerConnection | null {
    return this.pc;
  }

  get talk(): TalkState {
    return this._talk;
  }

  /** 設備在 ls-ctrl hello 宣告的對講能力；null = 還沒收到 hello（設備可能沒有 ls-ctrl，對講只靠麥克風）。 */
  get talkCapable(): boolean | null {
    return this.talkCtl?.talkCapable ?? null;
  }

  /** 伺服器的對講授權（start_viewing response 的 talk）；null = 還沒連上或伺服器沒說。 */
  get talkAllowed(): boolean | null {
    return this.talkGrant;
  }

  /** 多鏡頭：設備 hello 的 `caps.lens`；null = 還沒收到 / 沒宣告。 */
  get lensCaps(): number[] | null {
    return this.lensCaps_;
  }

  /** 多鏡頭：主連線最後一個 state 的鏡頭部分。 */
  get lensState(): LensState | null {
    return this.lensState_;
  }

  /** 多鏡頭：單畫面切換鏡頭的狀態；沒開 ls-ctrl = null。 */
  get lensPhase(): LensPhase | null {
    return this.lensSw?.phase ?? null;
  }

  /**
   * 換鏡頭（同一條連線 ls-ctrl `select {lens, video?}`）。回 false = 不能送（ls-ctrl 沒開 / 已經在顯示它）。
   * 成功 = state.lens_active 等於所選而且之後第一個影格到：UI 每收到一格就呼叫 lensFrame()。
   */
  selectLens(lens: number, video?: 'hd' | 'sd' | 'auto'): boolean {
    const sw = this.lensSw;
    const dc = this.ctrl;
    if (!sw || !dc || dc.readyState !== 'open') return false;
    const frame = sw.select(lens, Date.now(), video);
    if (!frame) return false;
    try {
      dc.send(frame);
    } catch {
      return false;
    }
    this.emitLensPhase();
    this.armLensTimer();
    return true;
  }

  /** 收到一個影格（video.requestVideoFrameCallback / timeupdate）；切換中等首幀用。 */
  lensFrame(): void {
    const sw = this.lensSw;
    if (!sw) return;
    const before = sw.phase;
    if (sw.onFrame() !== before) this.emitLensPhase();
  }

  private emitLensPhase(): void {
    if (this.lensSw) this.cb.onLensPhase?.(this.lensSw.phase);
  }

  private armLensTimer(): void {
    if (this.lensTimer) return;
    this.lensTimer = setInterval(() => {
      const sw = this.lensSw;
      if (!sw || sw.phase.kind !== 'switching') {
        this.clearLensTimer();
        return;
      }
      const before = sw.phase;
      if (sw.tick(Date.now()) !== before) {
        this.clearLensTimer();
        this.emitLensPhase();
      }
    }, 250);
  }

  private clearLensTimer(): void {
    if (this.lensTimer) clearInterval(this.lensTimer);
    this.lensTimer = null;
  }

  private onLensCtrl(pc: RTCPeerConnection, text: string): void {
    if (this.pc !== pc) return;
    const m = parseLsCtrlLens(text);
    const sw = this.lensSw;
    if (!m || !sw) return;
    const before = sw.phase;
    if (m.kind === 'hello') {
      this.lensCaps_ = m.lens;
      this.cb.onLensHello?.(m.lens);
      return;
    }
    if (m.state) {
      this.lensState_ = m.state;
      this.cb.onLensState?.(m.state);
    }
    if (m.kind === 'response') sw.onResponse(m.id, m.ok, m.err, m.reason, m.state);
    else if (m.state) sw.onState(m.state);
    if (sw.phase !== before) this.emitLensPhase();
  }

  private setTalkState(t: TalkState): void {
    if (this._talk === t) return;
    this._talk = t;
    this.cb.onTalk?.(t);
  }

  /**
   * 對講開 / 關。開：取麥克風 → 換上 audio sender → ls-ctrl `select talk:true`；關：拿掉軌、停麥克風 → `select talk:false`。
   * 沒連上、或這一路沒協商對講 → unavailable；伺服器說沒有 media.talk → forbidden（不取麥克風）。
   */
  async setTalk(on: boolean): Promise<void> {
    const pc = this.pc;
    const sender = this.audioSender;
    if (!on) {
      this.releaseMic();
      this.talkCtl?.setTalk(false);
      this.setTalkState('off');
      return;
    }
    if (!pc || !sender || this._phase !== 'connected') {
      this.setTalkState('unavailable');
      return;
    }
    if (this.talkGrant === false) {
      this.setTalkState('forbidden');
      return;
    }
    if (this._talk === 'on' || this._talk === 'starting') return;
    this.setTalkState('starting');
    let track: MediaStreamTrack | null = null;
    try {
      const ms = await this.getMicrophone();
      track = ms.getAudioTracks()[0] ?? null;
      if (!track) throw new KinioError(0, 'NO_MICROPHONE', 'no audio track');
    } catch (e) {
      const name = e instanceof Error ? e.name : '';
      if ((this._talk as TalkState) === 'starting') this.setTalkState(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'error');
      return;
    }
    if (this.pc !== pc || (this._talk as TalkState) !== 'starting') {
      track.stop(); // 取麥克風期間斷線 / 使用者已經放開
      return;
    }
    try {
      await sender.replaceTrack(track);
    } catch {
      track.stop();
      this.setTalkState('error');
      return;
    }
    if (this.pc !== pc || (this._talk as TalkState) !== 'starting') {
      track.stop();
      void sender.replaceTrack(null).catch(() => undefined);
      return;
    }
    this.mic = track;
    this.talkCtl?.setTalk(true);
    this.setTalkState('on');
  }

  private releaseMic(): void {
    const t = this.mic;
    this.mic = null;
    if (t) t.stop();
    const sender = this.audioSender;
    if (t && sender) void sender.replaceTrack(null).catch(() => undefined);
  }

  private setPhase(p: LivePhase): void {
    if (p === 'connected') {
      this.leaseRestarted = false;
      if (this.attempt_ && this.attempt_.connectedAt === undefined) this.attempt_.connectedAt = this.now_();
    }
    if (this._phase === p) return;
    this._phase = p;
    this.cb.onPhase?.(p);
  }

  private failWith(e: unknown): void {
    const err = e instanceof KinioError ? e : e instanceof Error ? new KinioError(0, 'LIVE_FAILED', e.message) : new KinioError(0, 'LIVE_FAILED', String(e));
    this.lastError = err;
    this.cb.onError?.(err);
    this.setPhase('failed');
  }

  /** 建立一路觀看。重複呼叫會先收掉舊的。 */
  async start(): Promise<void> {
    if (this.disposed) return;
    const gen = ++this.gen;
    this.teardown(false);
    this.lastError = null;
    this.attempt_ = { id: randomAttemptId(), startedAt: this.now_(), warm: (this.signal as { state?: string }).state === 'open', reported: false };
    this.setPhase('signaling');

    if (!this.PC) {
      this.failWith(new KinioError(0, 'NO_WEBRTC', 'RTCPeerConnection unavailable'));
      return;
    }

    try {
      await this.signal.ready();
      if (gen !== this.gen) return;

      // iceServers 留空：一律用 start_viewing response 裡的（契約 §4）。
      const pc = new this.PC();
      this.pc = pc;
      this.subscribeSignal(gen, pc);

      pc.addTransceiver('video', { direction: 'recvonly' });
      if (this.wantTalk) {
        // 對講：audio sendrecv、軌先空著（按下才換成麥克風，不按就不送聲音也不跳麥克風權限）
        const tx = pc.addTransceiver('audio', { direction: 'sendrecv' });
        this.audioSender = tx?.sender ?? null;
      } else if (this.wantAudio) pc.addTransceiver('audio', { direction: 'recvonly' });
      if (this.wantTalk || this.wantLensCtl) {
        // ls-ctrl 要在 createOffer 前開，offer 才有 m=application（lsrtc 收不到 talk:true 會丟掉送來的音訊；換鏡頭也走它）
        try {
          const dc = pc.createDataChannel(LS_CTRL_LABEL, { ordered: true, protocol: LS_CTRL_PROTOCOL });
          this.ctrl = dc;
          const tc = new TalkControl((frame) => {
            if (dc.readyState !== 'open') return false;
            try {
              dc.send(frame);
              return true;
            } catch {
              return false;
            }
          });
          tc.onHello = (capable) => {
            if (this.pc === pc) this.cb.onHello?.(capable);
          };
          tc.onRejected = (err) => {
            if (this.pc !== pc) return;
            this.releaseMic();
            this.setTalkState(err === 'TALK_BUSY' ? 'busy' : 'error');
          };
          this.talkCtl = tc;
          if (this.wantLensCtl) this.lensSw = new LensSwitch(this.lens_ ?? 0, LENS_ID_BASE);
          dc.onmessage = (ev: MessageEvent) => {
            if (this.pc !== pc || typeof ev.data !== 'string') return;
            tc.onMessage(ev.data);
            if (this.lensSw) this.onLensCtrl(pc, ev.data);
          };
        } catch {
          /* 沒有 DataChannel：對講只靠麥克風（CTL-001） */
        }
      }

      // MediaStream 只在真的收到 track 時才建（jsdom / 無 WebRTC 環境沒有這個建構子）。
      let fallbackStream: MediaStream | null = null;
      pc.ontrack = (ev: RTCTrackEvent) => {
        if (this.pc !== pc) return;
        let s = ev.streams[0] ?? null;
        if (!s) {
          try {
            fallbackStream = fallbackStream ?? new MediaStream();
            fallbackStream.addTrack(ev.track);
            s = fallbackStream;
          } catch {
            return;
          }
        }
        this.stream = s;
        this.cb.onStream?.(s);
      };

      pc.onicecandidate = (ev: RTCPeerConnectionIceEvent) => {
        if (this.pc !== pc) return;
        const cand = ev.candidate ? ev.candidate.toJSON() : null;
        if (this.sessionId) this.signal.sendIce(this.deviceRef, this.sessionId, cand);
        else this.pendingLocalIce.push(cand);
      };

      const onStateChange = () => {
        if (this.pc !== pc) return;
        this.cb.onConnectionState?.({ ice: pc.iceConnectionState, pc: pc.connectionState });
        const st = pc.connectionState;
        if (st === 'connected') this.setPhase('connected');
        else if (st === 'failed') {
          this.failWith(new KinioError(0, 'ICE_FAILED', 'peer connection failed'));
        } else if (st === 'connecting' && this._phase !== 'connected') this.setPhase('ice');
      };
      pc.onconnectionstatechange = onStateChange;
      pc.oniceconnectionstatechange = () => {
        if (this.pc !== pc) return;
        this.cb.onConnectionState?.({ ice: pc.iceConnectionState, pc: pc.connectionState });
        // connectionState 在某些瀏覽器晚於 iceConnectionState；兩邊都收斂。
        if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') this.setPhase('connected');
        else if (pc.iceConnectionState === 'failed') this.failWith(new KinioError(0, 'ICE_FAILED', 'ICE failed'));
      };

      const offer = await pc.createOffer();
      if (gen !== this.gen || this.pc !== pc) return;

      let ticket = '';
      if (this.reserve_) {
        const r = await this.reserve_();
        if (gen !== this.gen || this.pc !== pc) return;
        if (!r?.ticket) throw new KinioError(502, 'NO_SESSION_TICKET', 'live-sessions returned no session_ticket');
        ticket = r.ticket;
        this.reservedSessionId = r.sessionId ?? null;
      }
      this.setPhase('negotiating');
      attemptSeq += 1;
      const resp = await this.signal.request(
        this.deviceRef,
        'start_viewing',
        {
          sdp: { type: 'offer', sdp: offer.sdp },
          connection_attempt_id: `att-${Date.now().toString(36)}-${attemptSeq}`,
          ...(this.stream_ ? { stream: this.stream_ } : {}),
          ...(this.lens_ !== undefined ? { lens: this.lens_ } : {}),
          ...(this.primarySessionId_ ? { primary_session_id: this.primarySessionId_ } : {}),
          ...(ticket ? { session_ticket: ticket } : {}),
        },
        this.commandTimeoutMs,
      );
      if (gen !== this.gen || this.pc !== pc) return;

      const grant = extractGrant(resp.payload);
      this.sessionId = grant.sessionId;
      if (this.attempt_ && this.attempt_.answerAt === undefined) {
        this.attempt_.answerAt = this.now_();
        this.attempt_.sessionId = this.reservedSessionId ?? grant.sessionId ?? undefined;
      }
      if (this.primarySessionId_ && !companionEchoOk(this.lens_ ?? 0, this.primarySessionId_, grant.lens, grant.primarySessionId)) {
        // ① hub 回送核對（舊 hub 會連 primary_session_id 一起丟 → 設備當成第二個人）：關掉、當成伴隨失敗
        this.teardown(true);
        throw new KinioError(0, 'COMPANION_MISMATCH', 'hub did not echo lens / primary_session_id');
      }
      this.talkGrant = grant.talk;
      this.cb.onSession?.({ sessionId: grant.sessionId, talkGranted: grant.talk });
      if (grant.iceServers.length > 0) {
        try {
          pc.setConfiguration({ iceServers: grant.iceServers });
        } catch {
          /* 不支援就退回 host/srflx */
        }
      }
      await pc.setLocalDescription(offer);
      if (gen !== this.gen || this.pc !== pc) return;
      await pc.setRemoteDescription(grant.answer);
      if (gen !== this.gen || this.pc !== pc) return;

      this.hasRemoteVideo = sdpHasMedia(grant.answer.sdp, 'video');
      this.hasRemoteAudio = sdpHasMedia(grant.answer.sdp, 'audio');
      this.setPhase('ice');

      // answer 之前緩衝的本地 candidate（含 null = gathering 完成）現在才有 session_id 可帶。
      const queued = this.pendingLocalIce.splice(0);
      for (const c of queued) this.signal.sendIce(this.deviceRef, this.sessionId, c);
    } catch (e) {
      if (gen !== this.gen || this.disposed) return;
      this.teardown(false);
      this.failWith(e);
    }
  }

  private subscribeSignal(gen: number, pc: RTCPeerConnection): void {
    this.offs.push(
      this.signal.on('ice_candidate', (f: IceCandidateFrame) => {
        if (gen !== this.gen || this.pc !== pc) return;
        if (f.device_ref && f.device_ref !== this.deviceRef) return;
        if (f.session_id && this.sessionId && f.session_id !== this.sessionId) return;
        if (!f.candidate) return; // null = 對端 gathering 完成
        void pc.addIceCandidate(f.candidate).catch(() => {
          /* 過期 / 重複 candidate */
        });
      }),
    );
    this.offs.push(
      this.signal.on('error', (f: SignalErrorFrame) => {
        if (gen !== this.gen || this.pc !== pc) return;
        if (f.session_id && this.sessionId && f.session_id !== this.sessionId) return;
        if (!f.session_id && f.request_id) return; // 已由 request() 處理
        const code = (typeof f.code === 'string' && f.code) || 'SIGNAL_ERROR';
        if (isTokenExpired(code) && this.quietRestart()) return;
        this.teardown(false);
        this.failWith(signalError({ ...f, code: code.toUpperCase() }));
      }),
    );
    // media hub 把 session 綁在這條信令連線上：連線斷了（部署重啟、身分 session 被撤、網路）hub 就收掉這條連線的 session
    // 並對設備送 stop（client_gone），重連後也不會回來。已拿到 session_id 的觀看要當場失敗，交給呼叫端重連——
    // 不然 pc 可能停在 ICE checking（斷線時 trickle 的 candidate 丟了）而且永遠不會 failed。
    // 還在等 start_viewing response 的由 request() 的 SIGNAL_CLOSED 處理。
    this.offs.push(
      this.signal.on('state', (st: SignalState) => {
        if (st !== 'closed' || gen !== this.gen || this.pc !== pc || !this.sessionId) return;
        // 被撤權 / 身分 session 被撤是終態（呼叫端不該重連）；其他（部署重啟、網路）可重連
        const last = (this.signal as { lastClose?: { code: number; reason: string } | null }).lastClose;
        // 媒體租期到了（沒續上）：信令會安靜重連 → 這裡也安靜重開觀看，不顯示錯誤（一次；再失敗照常報錯）。
        const quiet = (this.signal as { quietReconnect?: boolean }).quietReconnect === true;
        const code = quiet ? 'TOKEN_EXPIRED' : closeErrorCode(last);
        if (code === 'TOKEN_EXPIRED' && this.quietRestart()) return;
        this.teardown(false);
        this.failWith(new KinioError(code === 'SESSION_REVOKED' || code === 'ACCESS_REVOKED' ? 403 : 0, code, `media signal closed (${last?.code ?? 0} ${last?.reason ?? ''})`));
      }),
    );
  }

  /** 租期到了：安靜重開一次（回 false = 已重開過，照常報錯）。 */
  private quietRestart(): boolean {
    if (this.leaseRestarted) return false;
    this.leaseRestarted = true;
    void this.start();
    return true;
  }

  private teardown(sendStop: boolean, reason?: LiveStopReason): void {
    for (const off of this.offs.splice(0)) off();
    this.releaseMic();
    this.audioSender = null;
    this.talkCtl = null;
    this.talkGrant = null;
    this.clearLensTimer();
    if (this.lensSw) this.lens_ = this.lensSw.showing;
    this.lensSw = null;
    this.lensCaps_ = null;
    this.lensState_ = null;
    const dc = this.ctrl;
    this.ctrl = null;
    if (dc) {
      dc.onmessage = null;
      try {
        dc.close();
      } catch {
        /* already closed */
      }
    }
    this.setTalkState('off');
    const sid = this.sessionId;
    this.sessionId = null;
    this.pendingLocalIce = [];
    if (sendStop && sid) {
      this.signal.request(this.deviceRef, 'stop_viewing', { session_id: sid, ...(reason ? { reason } : {}) }, 5000).catch(() => {
        /* best-effort：離開頁面時不擋 */
      });
    }
    const pc = this.pc;
    this.pc = null;
    if (pc) {
      pc.ontrack = null;
      pc.onicecandidate = null;
      pc.onconnectionstatechange = null;
      pc.oniceconnectionstatechange = null;
      try {
        pc.close();
      } catch {
        /* already closed */
      }
    }
    if (this.stream) {
      this.stream = null;
      this.cb.onStream?.(null);
    }
    this.hasRemoteVideo = false;
    this.hasRemoteAudio = false;
  }

  /** 停止觀看（送 stop_viewing；`reason` 帶給平台的連線品質統計，見 {@link LiveStopReason}）。 */
  stop(reason?: LiveStopReason): void {
    this.gen += 1;
    this.teardown(true, reason);
    this.setPhase('stopped');
  }

  /** 卸載：停止並不再回呼。 */
  dispose(reason?: LiveStopReason): void {
    this.disposed = true;
    this.gen += 1;
    this.teardown(true, reason);
    this._phase = 'stopped';
  }
}

/** UI 用：phase → 粗分類（connecting / connected / failed / stopped）。 */
export function livePhaseGroup(p: LivePhase): 'connecting' | 'connected' | 'failed' | 'stopped' {
  switch (p) {
    case 'connected':
      return 'connected';
    case 'failed':
      return 'failed';
    case 'stopped':
      return 'stopped';
    default:
      return 'connecting';
  }
}
